import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { DeleteModal } from '../../components/DeleteModal';
import { InsightDetailModal, type InsightDetailModalItem } from '../../components/InsightDetailModal';
import { useToast } from '../../components/Toast';
import { getLlmMeta } from '../../lib/llmModels';
import { supabase } from '../../lib/supabase';
import { isLocked } from '../../lib/workspaces';
import { getWorkspaceBrandName } from '../../lib/brandName';
import { decodeHtmlEntities } from '../../lib/htmlEntities';
import { useAuthStore } from '../../stores/authStore';
import type {
  Competitor,
  PromptResponse,
  PromptResponseCitation,
  PromptResponseMention,
  TrackedPrompt,
  Database,
} from '../../types/database';

type ResponseRow = Pick<
  PromptResponse,
  | 'id'
  | 'workspace_id'
  | 'prompt_id'
  | 'prompt_text_snapshot'
  | 'brand_mentioned'
  | 'competitors_mentioned'
  | 'created_at'
  | 'model'
>;

type MentionRow = Pick<
  PromptResponseMention,
  'prompt_response_id' | 'prompt_id' | 'brand_name' | 'brand_domain' | 'is_workspace_brand' | 'mentioned' | 'mention_count' | 'created_at'
>;

type CitationRow = Pick<
  PromptResponseCitation,
  | 'prompt_response_id'
  | 'prompt_id'
  | 'citation_url'
  | 'citation_domain'
  | 'citation_title'
  | 'source_type'
  | 'source_category'
  | 'associated_brand_domains'
  | 'associated_brand_names'
  | 'created_at'
>;

type InsightCard = {
  key: string;
  eyebrow: string;
  title: string;
  body: string;
  bullets: string[];
  promptTexts?: string[];
  models?: string[];
  sourceUrl?: string;
  sourceDomain?: string;
  brands?: Array<{
    name: string;
    domain: string;
    faviconUrl: string;
  }>;
};

type RecommendationStatus = 'recommended' | 'in_progress' | 'completed';

type RecommendationView = 'list' | 'kanban';

type AnalysisStatus = 'good' | 'needs_improvement' | 'needs_fixing';
type AnalysisVerdict = 'pass' | 'partial' | 'fail';

type AnalysisIssue = {
  severity?: 'P1' | 'P2' | 'P3' | 'P4';
};

type AnalysisSection = {
  status: AnalysisStatus;
  verdict?: AnalysisVerdict;
  issues?: AnalysisIssue[];
};

type AnalysisResult = {
  normalizedUrl: string;
  fetchedAt: string;
  overallScore: number;
  overallRating: 'Perfect' | 'Excellent' | 'Good' | 'OK' | 'Poor';
  statusCounts: Record<AnalysisStatus, number>;
  sections: AnalysisSection[];
};

type StoredRecommendationState = {
  card: InsightCard;
  status: RecommendationStatus;
  removed: boolean;
  firstDetectedAt: string;
  lastDetectedAt: string;
};

type StoredRecommendationMap = Record<string, StoredRecommendationState>;
type PersistedRecommendationRow = Database['public']['Tables']['workspace_insight_recommendations']['Row'];
const INSIGHTS_PAGE_SIZE = 15;

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const getOrdinalSuffix = (value: number) => {
  const mod100 = value % 100;
  if (mod100 >= 11 && mod100 <= 13) return 'th';
  const mod10 = value % 10;
  if (mod10 === 1) return 'st';
  if (mod10 === 2) return 'nd';
  if (mod10 === 3) return 'rd';
  return 'th';
};

const formatInsightsDate = (date: Date) =>
  `${DAY_NAMES[date.getDay()]} ${date.getDate()}${getOrdinalSuffix(date.getDate())} ${MONTH_NAMES[date.getMonth()]}`;

const toLocalDateKey = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const normalizeDomain = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/+$/, '')
    .split('/')[0];

const domainMatches = (inputDomain: string, targetDomain: string): boolean => {
  const input = normalizeDomain(inputDomain);
  const target = normalizeDomain(targetDomain);
  return input === target || input.endsWith(`.${target}`);
};

const toStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string');
};

const truncateList = (values: string[], max = 3) =>
  values.length <= max ? values.join(', ') : `${values.slice(0, max).join(', ')} +${values.length - max}`;

const formatNaturalList = (values: string[]) => {
  if (values.length === 0) return '';
  if (values.length === 1) return values[0];
  if (values.length === 2) return `${values[0]} and ${values[1]}`;
  return `${values.slice(0, -1).join(', ')}, and ${values[values.length - 1]}`;
};

const buildInsightDetailContent = (card: InsightCard, workspaceBrandName: string) => {
  if (card.key.startsWith('source-')) {
    const brands = card.brands?.map((brand) => brand.name) ?? [];
    const brandLabel = brands.length > 0 ? truncateList(brands, 3) : 'competing brands';
    const platformLabel = card.models && card.models.length > 0 ? formatNaturalList(card.models) : 'AI answer sets';
    const sourceLabel = card.sourceDomain ?? card.sourceUrl ?? 'this source';
    const promptLabel = card.promptTexts?.[0] ? ` Prompts like “${card.promptTexts[0]}” are already causing this source to be cited.` : '';

    if (card.eyebrow === 'Owned page') {
      return {
        opportunity:
          `${card.body}\n\nA competitor-owned landing or product page is being cited directly in ${platformLabel}. That usually means the page is doing a clear job of explaining a feature, workflow, or use case in a way that AI models can easily lift into answers. ${workspaceBrandName} should build or improve an equivalent page so the same intent can resolve to your site instead.${promptLabel}`,
        nextSteps: [
          'Review the cited competitor page and identify the exact feature angle or use case it is winning on.',
          `Create or improve the matching page on ${workspaceBrandName}, making the feature explanation, headings, and proof points easier to scan.`,
          'Add concise examples, use-case subheadings, and clearer navigation so AI systems can map the page to that search intent.',
        ],
      };
    }

    if (card.eyebrow === 'Content creation opportunity') {
      return {
        opportunity:
          `${card.body}\n\nThis citation is coming from an editorial content format that ${workspaceBrandName} could realistically publish too. That makes it a content creation signal, not just a brand mention. If AI systems are already reusing this kind of article structure, topic framing, and comparison pattern, creating a stronger version on ${workspaceBrandName} can improve your chances of being mentioned for the same intent.${promptLabel}`,
        nextSteps: [
          'Review the cited article and identify the exact content format it uses, such as best-of, alternatives, comparison, or how-to.',
          `Create an equivalent article on ${workspaceBrandName} that answers the same intent with clearer structure, fresher proof points, and more specific product relevance.`,
          'Use descriptive headings, short comparison sections, and concrete examples so the page is easier for AI systems to quote and summarize.',
        ],
      };
    }

    return {
      opportunity:
        `${card.body}\n\nThis third-party source is already being reused in AI answers when ${brandLabel} are mentioned. That makes it a strong candidate for outreach, contributed commentary, list inclusion, or product mention work that helps ${workspaceBrandName} appear in the same answer pattern. If a model already trusts ${sourceLabel}, earning coverage there can be materially more useful than publishing another generic page on your own site.${promptLabel}`,
      nextSteps: [
        'Review the cited page and identify the framing, proof points, and brand mentions that make the source reusable in AI answers.',
        `Decide whether ${workspaceBrandName} should pursue a mention on this exact source or on a similar publication in the same category.`,
        'Prepare a concrete angle for outreach, such as a comparison entry, expert quote, customer story, or feature inclusion that fits the source naturally.',
      ],
    };
  }

  if (card.key === 'ai-site-audit') {
    return {
      opportunity:
        `${card.body}\n\nThe AI site audit gives you a prioritized view of what may be limiting ${workspaceBrandName}'s visibility, including crawlability, metadata quality, structured data, content structure, and automation readiness.`,
      nextSteps: [
        'Run the audit and review the highest-priority issues first.',
        'Apply the fixes with the biggest impact on crawlability and content understanding.',
        'Re-run the audit after changes to confirm the site is more AI-readable.',
      ],
    };
  }

  return {
    opportunity:
      `${card.body}\n\n${card.bullets[0] ?? 'Review this opportunity and turn it into a concrete content or distribution change.'}`,
    nextSteps:
      card.bullets.slice(1).length > 0
        ? card.bullets.slice(1)
        : ['Translate this insight into a concrete content, source, or visibility action.'],
  };
};

const isOwnedArticleUrl = (url: string) => {
  const normalized = url.toLowerCase().trim();
  const withProtocol = /^https?:\/\//.test(normalized) ? normalized : `https://${normalized}`;

  try {
    const parsed = new URL(withProtocol);
    const hostname = parsed.hostname.toLowerCase();
    const pathSegments = parsed.pathname
      .toLowerCase()
      .split('/')
      .filter(Boolean);

    if (
      hostname.startsWith('blog.') ||
      hostname.startsWith('article.') ||
      hostname.startsWith('articles.') ||
      hostname.startsWith('resources.') ||
      hostname.startsWith('news.')
    ) {
      return true;
    }

    return pathSegments.some((segment) =>
      ['blog', 'article', 'articles', 'resources', 'news'].includes(segment)
    );
  } catch {
    return (
      normalized.includes('/blog') ||
      normalized.includes('/article') ||
      normalized.includes('/articles') ||
      normalized.includes('/resources') ||
      normalized.includes('/news')
    );
  }
};

type ContentOpportunityPattern = 'best-of' | 'comparison' | 'alternatives' | 'how-to' | 'use-case';

const detectContentOpportunityPattern = (title: string | null): ContentOpportunityPattern | null => {
  const normalizedTitle = decodeHtmlEntities(title ?? '').trim().toLowerCase();
  if (!normalizedTitle) return null;

  if (/\balternatives?\b/.test(normalizedTitle)) return 'alternatives';
  if (/\b(compare|comparison|versus|vs\.?)\b/.test(normalizedTitle)) return 'comparison';
  if (/\b(best|top)\b/.test(normalizedTitle)) return 'best-of';
  if (/\bhow to\b|\bguide\b|\bwhat is\b/.test(normalizedTitle)) return 'how-to';
  if (
    /\b(platforms?|tools?|software|solutions?|providers?)\s+for\b/.test(normalizedTitle) ||
    /\bfor\b.+\bteams?\b/.test(normalizedTitle)
  ) {
    return 'use-case';
  }

  return null;
};

const getContentOpportunityTitle = (pattern: ContentOpportunityPattern) => {
  switch (pattern) {
    case 'alternatives':
      return 'Alternative pages are being cited';
    case 'comparison':
      return 'Comparison content is being cited';
    case 'how-to':
      return 'How-to content is being cited';
    case 'use-case':
      return 'Use-case content is being cited';
    case 'best-of':
    default:
      return 'Best-of content is being cited';
  }
};

const getContentOpportunitySummary = (pattern: ContentOpportunityPattern) => {
  switch (pattern) {
    case 'alternatives':
      return 'This suggests alternative-style pages are helping brands appear in AI search results.';
    case 'comparison':
      return 'This suggests comparison-style content is helping brands appear in AI search results.';
    case 'how-to':
      return 'This suggests educational how-to content is helping brands appear in AI search results.';
    case 'use-case':
      return 'This suggests use-case content is helping brands appear in AI search results.';
    case 'best-of':
    default:
      return 'This suggests best-of roundup content is helping brands appear in AI search results.';
  }
};

const getCitationInsightType = (
  url: string,
  citationTitle: string | null,
  sourceCategory: PromptResponseCitation['source_category'],
  isOwnedByBrand: boolean
): string => {
  if (isOwnedByBrand && isOwnedArticleUrl(url) && detectContentOpportunityPattern(citationTitle)) {
    return 'Content creation opportunity';
  }
  if (isOwnedByBrand && isOwnedArticleUrl(url)) return 'Owned article';
  if (isOwnedByBrand) return 'Owned page';
  if (sourceCategory === 'social') return 'Social source';
  if (sourceCategory === 'news') return 'News article';
  if (sourceCategory === 'government') return 'Government source';
  return 'Tech source';
};

const isAnalysisResult = (value: unknown): value is AnalysisResult => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.normalizedUrl === 'string' &&
    typeof candidate.fetchedAt === 'string' &&
    typeof candidate.overallScore === 'number' &&
    typeof candidate.overallRating === 'string' &&
    typeof candidate.statusCounts === 'object'
  );
};

const getAnalysisSectionVerdict = (section: AnalysisSection): AnalysisVerdict => {
  if (section.verdict) return section.verdict;
  if (section.status === 'good') return 'pass';
  if (section.status === 'needs_improvement') return 'partial';
  return 'fail';
};

const getAuditRecommendationItemCount = (result: AnalysisResult | null) => {
  if (!result) return 0;

  return result.sections.reduce((total, section) => {
    const issueCount = Array.isArray(section.issues) ? section.issues.length : 0;
    if (issueCount > 0) return total + issueCount;
    return getAnalysisSectionVerdict(section) === 'pass' ? total : total + 1;
  }, 0);
};

const isInsightCardPayload = (value: unknown): value is InsightCard => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.key === 'string' &&
    typeof candidate.eyebrow === 'string' &&
    typeof candidate.title === 'string' &&
    typeof candidate.body === 'string' &&
    Array.isArray(candidate.bullets)
  );
};

export function Insights() {
  const { workspace, viewingAs } = useAuthStore();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const workspaceId = workspace?.id ?? null;
  const workspaceBrandName = getWorkspaceBrandName(workspace);
  const workspaceBrandDomain = workspace?.brand_url ? normalizeDomain(workspace.brand_url) : '';
  const insightsDateLabel = useMemo(() => formatInsightsDate(new Date()), []);
  const storageDateKey = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const viewStorageKey = useMemo(
    () => `lima_insights_view:${workspaceId ?? 'no-workspace'}`,
    [workspaceId]
  );
  const seenStorageKey = useMemo(
    () => `lima_insights_seen:${workspaceId ?? 'no-workspace'}:${storageDateKey}`,
    [workspaceId, storageDateKey]
  );
  const rangeStartIso = useMemo(() => new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(), []);
  const [viewMode, setViewMode] = useState<RecommendationView>('list');
  const [draggedCardKey, setDraggedCardKey] = useState<string | null>(null);
  const [activeDropColumn, setActiveDropColumn] = useState<RecommendationStatus | null>(null);
  const [cardToDelete, setCardToDelete] = useState<InsightCard | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [cardToComplete, setCardToComplete] = useState<InsightCard | null>(null);
  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);
  const [activeInsight, setActiveInsight] = useState<InsightDetailModalItem | null>(null);
  const [showNewBadges, setShowNewBadges] = useState(true);
  const [insightsPage, setInsightsPage] = useState(1);
  const dragPreviewRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      const raw = window.localStorage.getItem(viewStorageKey);
      setViewMode(raw === 'kanban' ? 'kanban' : 'list');
    } catch {
      setViewMode('list');
    }
  }, [viewStorageKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(viewStorageKey, viewMode);
  }, [viewMode, viewStorageKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      const hasSeenInsights = window.localStorage.getItem(seenStorageKey) === '1';
      if (hasSeenInsights) {
        setShowNewBadges(false);
        return;
      }

      setShowNewBadges(true);

      const frame = window.requestAnimationFrame(() => {
        window.localStorage.setItem(seenStorageKey, '1');
        setShowNewBadges(false);
      });

      return () => window.cancelAnimationFrame(frame);
    } catch {
      setShowNewBadges(false);
    }
  }, [seenStorageKey]);

  const { data: competitors = [] } = useQuery({
    queryKey: ['insights-competitors', workspaceId],
    enabled: Boolean(workspaceId),
    queryFn: async (): Promise<Competitor[]> => {
      const { data, error } = await supabase
        .from('competitors')
        .select('*')
        .eq('workspace_id', workspaceId!)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: prompts = [] } = useQuery({
    queryKey: ['insights-prompts', workspaceId],
    enabled: Boolean(workspaceId),
    queryFn: async (): Promise<TrackedPrompt[]> => {
      const { data, error } = await supabase
        .from('tracked_prompts')
        .select('*')
        .eq('workspace_id', workspaceId!)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: responses = [] } = useQuery({
    queryKey: ['insights-responses', workspaceId, rangeStartIso],
    enabled: Boolean(workspaceId),
    queryFn: async (): Promise<ResponseRow[]> => {
      const { data, error } = await supabase
        .from('prompt_responses')
        .select('id, workspace_id, prompt_id, prompt_text_snapshot, brand_mentioned, competitors_mentioned, created_at, model')
        .eq('workspace_id', workspaceId!)
        .gte('created_at', rangeStartIso)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data ?? []) as ResponseRow[];
    },
  });

  const { data: mentions = [] } = useQuery({
    queryKey: ['insights-mentions', workspaceId, rangeStartIso],
    enabled: Boolean(workspaceId),
    queryFn: async (): Promise<MentionRow[]> => {
      const { data, error } = await supabase
        .from('prompt_response_mentions')
        .select('prompt_response_id, prompt_id, brand_name, brand_domain, is_workspace_brand, mentioned, mention_count, created_at')
        .eq('workspace_id', workspaceId!)
        .gte('created_at', rangeStartIso)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data ?? []) as MentionRow[];
    },
  });

  const { data: citations = [] } = useQuery({
    queryKey: ['insights-citations', workspaceId, rangeStartIso],
    enabled: Boolean(workspaceId),
    queryFn: async (): Promise<CitationRow[]> => {
      const { data, error } = await supabase
        .from('prompt_response_citations')
        .select(
          'prompt_response_id, prompt_id, citation_url, citation_domain, citation_title, source_type, source_category, associated_brand_domains, associated_brand_names, created_at'
        )
        .eq('workspace_id', workspaceId!)
        .gte('created_at', rangeStartIso)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data ?? []) as CitationRow[];
    },
  });

  const { data: storedAudit = null } = useQuery({
    queryKey: ['insights-ai-audit', workspaceId],
    enabled: Boolean(workspaceId),
    queryFn: async (): Promise<Database['public']['Tables']['workspace_ai_analyses']['Row'] | null> => {
      const { data, error } = await supabase
        .from('workspace_ai_analyses')
        .select('*')
        .eq('workspace_id', workspaceId!)
        .maybeSingle();

      if (error) throw error;
      return data ?? null;
    },
  });

  const { data: persistedRecommendationRows = [] } = useQuery({
    queryKey: ['insights-recommendations', workspaceId],
    enabled: Boolean(workspaceId),
    queryFn: async (): Promise<PersistedRecommendationRow[]> => {
      const { data, error } = await supabase
        .from('workspace_insight_recommendations')
        .select(
          'id, workspace_id, recommendation_key, recommendation_payload, status, is_removed, first_detected_at, last_detected_at, created_at, updated_at'
        )
        .eq('workspace_id', workspaceId!)
        .order('last_detected_at', { ascending: false });

      if (error) throw error;
      return data ?? [];
    },
  });

  const recommendationState = useMemo<StoredRecommendationMap>(
    () =>
      persistedRecommendationRows.reduce<StoredRecommendationMap>((accumulator, row) => {
        const payload = row.recommendation_payload;
        if (!isInsightCardPayload(payload)) {
          return accumulator;
        }

        accumulator[row.recommendation_key] = {
          card: {
            ...payload,
            key: row.recommendation_key,
          },
          status: row.status,
          removed: row.is_removed,
          firstDetectedAt: row.first_detected_at ?? row.created_at,
          lastDetectedAt: row.last_detected_at,
        };
        return accumulator;
      }, {}),
    [persistedRecommendationRows]
  );

  const recommendationStateMutation = useMutation({
    mutationFn: async ({
      key,
      status,
      removed,
    }: {
      key: string;
      status: RecommendationStatus;
      removed: boolean;
    }) => {
      const existing = recommendationState[key];
      const latestCard = existing?.card ?? freshInsights.cards.find((card) => card.key === key);
      const { error } = await supabase.from('workspace_insight_recommendations').upsert(
        {
          workspace_id: workspaceId!,
          recommendation_key: key,
          recommendation_payload: latestCard ?? {
            key,
            eyebrow: '',
            title: '',
            body: '',
            bullets: [],
          },
          status,
          is_removed: removed,
          updated_at: new Date().toISOString(),
        },
        {
          onConflict: 'workspace_id,recommendation_key',
        }
      );

      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['insights-recommendations', workspaceId] });
      await queryClient.invalidateQueries({ queryKey: ['sidebar_insights_count', workspaceId] });
    },
    onError: () => {
      toast.error('Unable to update this recommendation right now.');
    },
  });

  const promptMap = useMemo(
    () => new Map(prompts.map((prompt) => [prompt.id, prompt.text])),
    [prompts]
  );

  const competitorMeta = useMemo(
    () =>
      competitors.map((competitor) => ({
        name: competitor.name,
        domain: normalizeDomain(competitor.domain),
        faviconUrl:
          competitor.favicon_url ||
          `https://www.google.com/s2/favicons?domain=${encodeURIComponent(normalizeDomain(competitor.domain))}&sz=64`,
      })),
    [competitors]
  );

  const auditResult = useMemo(() => {
    const raw = storedAudit?.analysis_result ?? null;
    return isAnalysisResult(raw) ? raw : null;
  }, [storedAudit?.analysis_result]);

  const freshInsights = useMemo(() => {
    const totalPromptSearches = responses.length;
    const responseMap = new Map(responses.map((response) => [response.id, response]));
    const workspaceMentionResponseIds = new Set<string>();
    const competitorMentionResponseIds = new Set<string>();
    const competitorNamesByDomain = new Map<string, string>();

    for (const competitor of competitorMeta) {
      competitorNamesByDomain.set(competitor.domain, competitor.name);
    }

    for (const mention of mentions) {
      const mentionDomain = normalizeDomain(mention.brand_domain);
      if (!mention.mentioned) continue;

      if (mention.is_workspace_brand || (workspaceBrandDomain && mentionDomain === workspaceBrandDomain)) {
        workspaceMentionResponseIds.add(mention.prompt_response_id);
      }

      if (competitorNamesByDomain.has(mentionDomain)) {
        competitorMentionResponseIds.add(mention.prompt_response_id);
      }
    }

    for (const response of responses) {
      if (response.brand_mentioned) {
        workspaceMentionResponseIds.add(response.id);
      }

      const legacyCompetitors = Array.isArray(response.competitors_mentioned) ? response.competitors_mentioned : [];
      for (const entry of legacyCompetitors) {
        if (!entry || typeof entry !== 'object') continue;
        const domainValue = 'domain' in entry && typeof entry.domain === 'string' ? normalizeDomain(entry.domain) : '';
        const mentioned = 'mentioned' in entry ? Boolean(entry.mentioned) : true;
        if (domainValue && mentioned && competitorNamesByDomain.has(domainValue)) {
          competitorMentionResponseIds.add(response.id);
        }
      }
    }

    const sourceOpportunities = new Map<
      string,
      {
        count: number;
        brand: {
          name: string;
          domain: string;
          faviconUrl: string;
        };
        prompts: Set<string>;
        models: Set<string>;
        sourceType: string;
        sourceCategory: PromptResponseCitation['source_category'];
        url: string;
        domain: string;
        title: string | null;
        isOwnedByBrand: boolean;
        lastSeenAt: string;
      }
    >();

    for (const citation of citations) {
      const associatedDomains = toStringArray(citation.associated_brand_domains).map(normalizeDomain);
      const associatedNames = toStringArray(citation.associated_brand_names);
      const associationCount = Math.max(associatedDomains.length, associatedNames.length);

      Array.from({ length: associationCount }).forEach((_, index) => {
        const brandDomain = associatedDomains[index] ?? '';
        const fallbackName = associatedNames[index] ?? competitorNamesByDomain.get(brandDomain) ?? brandDomain;
        const brandName = typeof fallbackName === 'string' ? fallbackName.trim() : '';
        if (!brandName && !brandDomain) return;
        if (workspaceBrandDomain && brandDomain === workspaceBrandDomain) return;
        if (brandName.toLowerCase() === workspaceBrandName.trim().toLowerCase()) return;

        const brandMeta =
          competitorMeta.find((competitor) => competitor.domain === brandDomain) ?? {
            name: brandName || brandDomain,
            domain: brandDomain,
            faviconUrl: brandDomain
              ? `https://www.google.com/s2/favicons?domain=${encodeURIComponent(brandDomain)}&sz=64`
              : '',
          };
        const promptText =
          (citation.prompt_id ? promptMap.get(citation.prompt_id) : null) ??
          responseMap.get(citation.prompt_response_id)?.prompt_text_snapshot ??
          null;
        const responseModel = responseMap.get(citation.prompt_response_id)?.model;
        const isOwnedByBrand = Boolean(brandDomain) && domainMatches(citation.citation_domain, brandDomain);
        if (isOwnedByBrand) return;

        const recommendationKey = `source:${brandDomain || brandName.toLowerCase()}:${citation.citation_url}`;
        const existing = sourceOpportunities.get(recommendationKey) ?? {
          count: 0,
          brand: brandMeta,
          prompts: new Set<string>(),
          models: new Set<string>(),
          sourceType: citation.source_type,
          sourceCategory: citation.source_category,
          url: citation.citation_url,
          domain: normalizeDomain(citation.citation_domain || citation.citation_url),
          title: decodeHtmlEntities(citation.citation_title),
          isOwnedByBrand,
          lastSeenAt: citation.created_at,
        };

        existing.count += 1;
        existing.brand = brandMeta;
        existing.sourceType = citation.source_type;
        existing.sourceCategory = citation.source_category;
        existing.isOwnedByBrand = isOwnedByBrand;
        existing.lastSeenAt =
          citation.created_at > existing.lastSeenAt ? citation.created_at : existing.lastSeenAt;
        if (promptText) existing.prompts.add(promptText);
        if (responseModel) existing.models.add(responseModel);
        sourceOpportunities.set(recommendationKey, existing);
      });
    }

    const cards: InsightCard[] = Array.from(sourceOpportunities.entries())
      .sort((a, b) => {
        if (b[1].count !== a[1].count) return b[1].count - a[1].count;
        return b[1].lastSeenAt.localeCompare(a[1].lastSeenAt);
      })
      .map(([key, source]) => {
        const prompts = Array.from(source.prompts);
        const modelNames = Array.from(source.models);
        const platformLabel = formatNaturalList(modelNames);
        const primaryBrand = source.brand.name || 'A competitor';
        const insightType = getCitationInsightType(source.url, source.title, source.sourceCategory, source.isOwnedByBrand);
        const sourceLabel = decodeHtmlEntities(source.title) || source.url;
        const contentPattern = detectContentOpportunityPattern(source.title);

        return {
          key,
          eyebrow: insightType,
          title:
            insightType === 'Owned page'
                ? `${primaryBrand} is directly mentioned`
                : insightType === 'Content creation opportunity'
                  ? getContentOpportunityTitle(contentPattern ?? 'best-of')
                : insightType === 'Owned article'
                  ? `${primaryBrand}'s article is mentioned on ${platformLabel || 'AI platforms'}`
                : `${primaryBrand} is featured on ${source.domain}`,
          body:
            insightType === 'Owned page'
                ? `${primaryBrand} is directly featured on ${platformLabel || 'AI platforms'} with the page titled '${sourceLabel}'.`
                : insightType === 'Content creation opportunity'
                  ? `A page titled '${sourceLabel}' on ${source.domain} is mentioning ${primaryBrand} and is being cited by ${platformLabel || 'AI platforms'}. ${getContentOpportunitySummary(contentPattern ?? 'best-of')}`
                : insightType === 'Owned article'
                  ? `A blog article titled '${sourceLabel}' is being mentioned by ${platformLabel || 'AI platforms'}.`
                : `A page titled '${sourceLabel}' on ${source.domain} is mentioning ${primaryBrand} and is being cited by ${platformLabel || 'AI platforms'}.`,
          bullets:
            insightType === 'Owned page'
                ? [
                    prompts.length > 0
                      ? `Prompts like ${truncateList(prompts.map((prompt) => `“${prompt}”`), 2)} are already causing this direct competitor page to be cited.`
                    : 'This competitor page is already being cited directly in AI answers.',
                    `Build or improve the equivalent page on ${workspaceBrandName} so the same intent can resolve to your site instead.`,
                    'Make the page easier to quote with clearer headings, concrete feature explanations, and stronger proof points.',
                  ]
                : insightType === 'Content creation opportunity'
                  ? [
                    prompts.length > 0
                      ? `Prompts like ${truncateList(prompts.map((prompt) => `“${prompt}”`), 2)} are already causing this editorial page to be cited${source.domain === source.brand.domain ? '' : ` while mentioning ${primaryBrand}`}.`
                      : `This editorial page is already being reused in AI answers${source.domain === source.brand.domain ? '' : ` while mentioning ${primaryBrand}`}.`,
                    `Create a similar ${contentPattern === 'comparison' ? 'comparison' : contentPattern === 'alternatives' ? 'alternatives' : contentPattern === 'how-to' ? 'how-to' : contentPattern === 'use-case' ? 'use-case' : 'best-of'} article on ${workspaceBrandName} that targets the same search intent.`,
                    'Use a clear heading structure, concrete examples, and strong comparisons so the content is easier for AI systems to lift into answers.',
                  ]
                : insightType === 'Owned article'
                  ? [
                      prompts.length > 0
                        ? `Prompts like ${truncateList(prompts.map((prompt) => `“${prompt}”`), 2)} are already causing this competitor article to be cited.`
                        : 'This competitor-owned article is already being reused in AI answers.',
                      `Create a directly competing article on ${workspaceBrandName} that answers the same intent more clearly and completely.`,
                      'Mirror the winning structure with stronger proof points, sharper comparisons, and more quotable sections.',
                    ]
                : [
                    prompts.length > 0
                      ? `Prompts like ${truncateList(prompts.map((prompt) => `“${prompt}”`), 2)} are already causing this source to be cited while mentioning ${primaryBrand}.`
                      : `This third-party source is already being reused in AI answers while mentioning ${primaryBrand}.`,
                    `Work out how ${workspaceBrandName} can earn a mention, quote, or comparable coverage on this source or a similar one.`,
                    'Prepare a concrete outreach or inclusion angle that fits the page naturally instead of forcing a generic backlink request.',
                  ],
          promptTexts: prompts.slice(0, 4),
          models: modelNames,
          sourceUrl: source.url,
          sourceDomain: source.domain,
          brands: [source.brand],
        } satisfies InsightCard;
      });

    if (!auditResult) {
      cards.push({
        key: 'ai-site-audit',
        eyebrow: 'AI site audit',
        title: 'Run an AI site audit on Lima for free',
        body: `Check how visible, crawlable, and AI-ready ${workspaceBrandName} is across metadata, structured data, content clarity, and automation readiness.`,
        bullets: [
          'Run the audit and review the highest-priority fixes first.',
          'Use the results to improve crawlability, metadata, and AI visibility over time.',
        ],
      });
    }

    return {
      totalPromptSearches,
      workspaceMentionedSearches: workspaceMentionResponseIds.size,
      competitorMentionedSearches: competitorMentionResponseIds.size,
      cards,
    };
  }, [responses, mentions, citations, promptMap, competitorMeta, workspaceBrandDomain, workspaceBrandName, auditResult]);

  const syncSignatureRef = useRef<string>('');

  useEffect(() => {
    // A Locked workspace can't be written to, so there's nothing to sync until it's paid for.
    // Nor can anything during View as; the page shows the fresh cards either way.
    if (!workspaceId || isLocked(workspace) || viewingAs || freshInsights.cards.length === 0) return;

    const signature = JSON.stringify(
      freshInsights.cards.map((card) => ({
        key: card.key,
        eyebrow: card.eyebrow,
        title: card.title,
        body: card.body,
        bullets: card.bullets,
        promptTexts: card.promptTexts ?? [],
        models: card.models ?? [],
        sourceUrl: card.sourceUrl ?? '',
        sourceDomain: card.sourceDomain ?? '',
      }))
    );

    if (syncSignatureRef.current === signature) return;
    syncSignatureRef.current = signature;

    void (async () => {
      const now = new Date().toISOString();
      const { error } = await supabase.from('workspace_insight_recommendations').upsert(
        freshInsights.cards.map((card) => ({
          workspace_id: workspaceId,
          recommendation_key: card.key,
          recommendation_payload: card,
          last_detected_at: now,
          updated_at: now,
        })),
        {
          onConflict: 'workspace_id,recommendation_key',
        }
      );

      if (error) {
        syncSignatureRef.current = '';
        toast.error('Unable to sync the latest insights right now.');
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['insights-recommendations', workspaceId] });
      await queryClient.invalidateQueries({ queryKey: ['sidebar_insights_count', workspaceId] });
    })();
  }, [freshInsights.cards, queryClient, toast, workspace, workspaceId, viewingAs]);

  const mergedCards = useMemo(() => {
    const merged = new Map<string, StoredRecommendationState>();

    Object.entries(recommendationState).forEach(([key, value]) => {
      if (key === 'ai-site-audit' && auditResult) return;
      merged.set(key, value);
    });

    freshInsights.cards.forEach((card) => {
      const existing = merged.get(card.key);
      merged.set(card.key, {
        card,
        status: existing?.status ?? 'recommended',
        removed: existing?.removed ?? false,
        firstDetectedAt: existing?.firstDetectedAt ?? new Date().toISOString(),
        lastDetectedAt: existing?.lastDetectedAt ?? new Date().toISOString(),
      });
    });

    return Array.from(merged.values()).sort((a, b) => b.lastDetectedAt.localeCompare(a.lastDetectedAt));
  }, [auditResult, freshInsights.cards, recommendationState]);

  const visibleEntries = useMemo(
    () => mergedCards.filter((entry) => !entry.removed),
    [mergedCards]
  );

  const visibleCards = useMemo(
    () => visibleEntries.map((entry) => entry.card),
    [visibleEntries]
  );

  const getRecommendationStatus = (key: string): RecommendationStatus =>
    recommendationState[key]?.status ?? 'recommended';

  const activeRecommendationCount = useMemo(
    () => visibleCards.filter((card) => getRecommendationStatus(card.key) !== 'completed').length,
    [visibleCards, recommendationState]
  );

  const visibleEntriesByStatus = useMemo(() => {
    const grouped: Record<RecommendationStatus, typeof visibleEntries> = {
      recommended: [],
      in_progress: [],
      completed: [],
    };

    for (const entry of visibleEntries) {
      grouped[getRecommendationStatus(entry.card.key)].push(entry);
    }

    const sortEntries = (entries: typeof visibleEntries) =>
      [...entries].sort((a, b) => {
        const aIsToday = toLocalDateKey(new Date(a.firstDetectedAt)) === storageDateKey;
        const bIsToday = toLocalDateKey(new Date(b.firstDetectedAt)) === storageDateKey;

        if (aIsToday !== bIsToday) {
          return aIsToday ? -1 : 1;
        }

        return b.lastDetectedAt.localeCompare(a.lastDetectedAt);
      });

    return {
      recommended: sortEntries(grouped.recommended),
      in_progress: sortEntries(grouped.in_progress),
      completed: sortEntries(grouped.completed),
    };
  }, [visibleEntries, recommendationState, storageDateKey]);

  const sortedVisibleEntries = useMemo(() => {
    const order: RecommendationStatus[] = ['recommended', 'in_progress', 'completed'];
    return [...visibleEntries].sort(
      (a, b) => order.indexOf(getRecommendationStatus(a.card.key)) - order.indexOf(getRecommendationStatus(b.card.key))
    );
  }, [visibleEntries, recommendationState]);

  const listVisibleEntries = useMemo(
    () => sortedVisibleEntries.filter((entry) => getRecommendationStatus(entry.card.key) !== 'completed'),
    [sortedVisibleEntries, recommendationState]
  );

  const todaysRecommendationEntries = useMemo(
    () => listVisibleEntries.filter((entry) => toLocalDateKey(new Date(entry.firstDetectedAt)) === storageDateKey),
    [listVisibleEntries, storageDateKey]
  );

  const previousRecommendationEntries = useMemo(
    () => listVisibleEntries.filter((entry) => toLocalDateKey(new Date(entry.firstDetectedAt)) !== storageDateKey),
    [listVisibleEntries, storageDateKey]
  );

  const todaysRecommendationCards = useMemo(
    () => todaysRecommendationEntries.map((entry) => entry.card),
    [todaysRecommendationEntries]
  );

  const pagedRecommendationEntries = todaysRecommendationEntries.length > 0
    ? previousRecommendationEntries
    : listVisibleEntries;

  const totalInsightPages = Math.max(1, Math.ceil(pagedRecommendationEntries.length / INSIGHTS_PAGE_SIZE));
  const hasInsightsPagination = pagedRecommendationEntries.length > INSIGHTS_PAGE_SIZE;

  useEffect(() => {
    setInsightsPage((page) => Math.min(page, totalInsightPages));
  }, [totalInsightPages]);

  const paginatedListVisibleCards = useMemo(() => {
    const startIndex = (insightsPage - 1) * INSIGHTS_PAGE_SIZE;
    return pagedRecommendationEntries
      .slice(startIndex, startIndex + INSIGHTS_PAGE_SIZE)
      .map((entry) => entry.card);
  }, [insightsPage, pagedRecommendationEntries]);

  const auditRecommendationItemCount = useMemo(
    () => getAuditRecommendationItemCount(auditResult),
    [auditResult]
  );
  const updateRecommendationStatus = async (key: string, status: RecommendationStatus) => {
    const current = recommendationState[key];
    await recommendationStateMutation.mutateAsync({
      key,
      status,
      removed: current?.removed ?? false,
    });
  };

  const openDeleteCardModal = (card: InsightCard) => {
    setCardToDelete(card);
    setIsDeleteModalOpen(true);
  };

  const openCompleteCardModal = (card: InsightCard) => {
    if (getRecommendationStatus(card.key) === 'completed') return;
    setCardToComplete(card);
    setIsCompleteModalOpen(true);
  };

  const openInsightDetail = (card: InsightCard) => {
    const { opportunity, nextSteps } = buildInsightDetailContent(card, workspaceBrandName);
    const detectedAt =
      recommendationState[card.key]?.lastDetectedAt ??
      mergedCards.find((entry) => entry.card.key === card.key)?.lastDetectedAt ??
      new Date().toISOString();
    setActiveInsight({
      key: card.key,
      eyebrow: card.eyebrow,
      title: card.title,
      opportunity,
      nextSteps,
      promptTexts: card.promptTexts ?? [],
      searchedAt: detectedAt,
      models:
        card.models?.map((model) => {
          const meta = getLlmMeta(model);
          return {
            name: meta.name,
            short: meta.short,
            icon: meta.icon,
          };
        }) ?? [],
      brands: card.brands,
    });
  };

  const handleConfirmDeleteCard = async () => {
    if (!cardToDelete) return;
    const current = recommendationState[cardToDelete.key];
    await recommendationStateMutation.mutateAsync({
      key: cardToDelete.key,
      status: current?.status ?? 'recommended',
      removed: true,
    });
    setIsDeleteModalOpen(false);
    setCardToDelete(null);
  };

  const handleConfirmCompleteCard = async () => {
    if (!cardToComplete) return;
    await updateRecommendationStatus(cardToComplete.key, 'completed');
    setIsCompleteModalOpen(false);
    setCardToComplete(null);
  };

  const handleStatusAction = async (key: string) => {
    const status = getRecommendationStatus(key);
    if (status === 'recommended' || status === 'in_progress') {
      const card = visibleCards.find((item) => item.key === key);
      if (card) {
        openCompleteCardModal(card);
      }
      return;
    }
    await updateRecommendationStatus(key, 'in_progress');
  };

  const getStatusActionLabel = (key: string) => {
    const status = getRecommendationStatus(key);
    if (status === 'recommended') return 'Done';
    if (status === 'in_progress') return 'Done';
    return 'Move back';
  };

  const getInsightCategoryLabel = (card: InsightCard) => {
    if (card.key === 'ai-site-audit') return 'AI site audit';
    if (card.key.startsWith('source-')) {
      if (card.eyebrow === 'Owned page') return 'Direct feature opportunity';
      if (card.eyebrow === 'Content creation opportunity') {
        return `Content creation opportunity${card.sourceDomain ? `: ${card.sourceDomain}` : ''}`;
      }
      if (card.eyebrow === 'Owned article') return 'Article opportunity';
      if (card.eyebrow === 'News article') {
        return `News website feature opportunity${card.sourceDomain ? `: ${card.sourceDomain}` : ''}`;
      }
      if (card.eyebrow === 'Government source') {
        return `Government website feature opportunity${card.sourceDomain ? `: ${card.sourceDomain}` : ''}`;
      }
      if (card.eyebrow === 'Social source') {
        return `Social feature opportunity${card.sourceDomain ? `: ${card.sourceDomain}` : ''}`;
      }
      return `Tech website feature opportunity${card.sourceDomain ? `: ${card.sourceDomain}` : ''}`;
    }

    return 'Tech website feature opportunity';
  };

  const handleListDoneAction = async (key: string) => {
    if (getRecommendationStatus(key) === 'completed') return;
    const card = visibleCards.find((item) => item.key === key);
    if (card) {
      openCompleteCardModal(card);
    }
  };

  const isAuditRecommendation = (card: InsightCard) => card.key === 'ai-site-audit';

  const clearDragPreview = () => {
    if (dragPreviewRef.current) {
      dragPreviewRef.current.remove();
      dragPreviewRef.current = null;
    }
  };

  const renderViewIcon = (view: RecommendationView) =>
    view === 'list' ? (
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect width="18" height="18" x="3" y="3" rx="2" />
        <path d="M21 9H3" />
        <path d="M21 15H3" />
      </svg>
    ) : (
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect width="18" height="18" x="3" y="3" rx="2" />
        <path d="M9 3v18" />
        <path d="M15 3v18" />
      </svg>
    );

  const renderHeroPanel = (isKanban = false) => (
    <div className="card is-art-board is-insights-hero-card">
      <div className={`small-card is-gradient-card insights-hero-panel${isKanban ? ' is-kanban-layout' : ''}`}>
        <div className={`insights-hero-main${isKanban ? ' is-kanban-layout' : ''}`}>
          {isKanban ? (
            <div className="insights-hero-kanban-grid">
              <div className="insights-hero-kanban-copy">
                <div className="text-size-medium text-weight-semibold">
                  There are <span className="large-pill">{activeRecommendationCount}</span> insights for you to review
                </div>
                <div className="spacer-xxsmall"></div>
                <div className="line-divider is-white"></div>
                <div className="spacer-xxsmall"></div>
                <div className="text-size-regular">
                  <span className="text-weight-semibold">{workspaceBrandName}</span> has been mentioned in{' '}
                  <span className="text-weight-semibold">
                    {freshInsights.workspaceMentionedSearches} out of {freshInsights.totalPromptSearches}
                  </span>{' '}
                  prompt searches in the last 30 days
                </div>
                <div className="spacer-xsmall"></div>
                <div className="text-size-regular">
                  Your <span className="text-weight-semibold">competitors</span> have been mentioned in{' '}
                  <span className="text-weight-semibold">
                    {freshInsights.competitorMentionedSearches} out of {freshInsights.totalPromptSearches}
                  </span>{' '}
                  prompt searches in the last 30 days
                </div>
              </div>
              <div className="insight-body">
                <div className="text-size-small text-colour-white text-weight-semibold">What are insights?</div>
                <div className="spacer-tiny"></div>
                <div className="text-size-small text-weight-regular">
                  Insights help you to understand how competitors are appearing in AI search results so that you can create
                  your own ranking strategies.
                  <br />
                  <br />
                  Insights update regularly (we check every 24 hours), come back soon for more insights.
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="text-size-medium text-weight-semibold">
                There are <span className="large-pill">{activeRecommendationCount}</span> insights for you to review
              </div>
              <div className="spacer-xxsmall"></div>
              <div className="line-divider is-white"></div>
              <div className="spacer-xxsmall"></div>
              <div className="text-size-regular">
                <span className="text-weight-semibold">{workspaceBrandName}</span> has been mentioned in{' '}
                <span className="text-weight-semibold">
                  {freshInsights.workspaceMentionedSearches} out of {freshInsights.totalPromptSearches}
                </span>{' '}
                prompt searches in the last 30 days
              </div>
              <div className="spacer-xsmall"></div>
              <div className="text-size-regular">
                Your <span className="text-weight-semibold">competitors</span> have been mentioned in{' '}
                <span className="text-weight-semibold">
                  {freshInsights.competitorMentionedSearches} out of {freshInsights.totalPromptSearches}
                </span>{' '}
                prompt searches in the last 30 days
              </div>
              <div className="spacer-xxsmall"></div>
              <div className="line-divider is-white"></div>
              <div className="spacer-xxsmall"></div>
              <div className="insight-body">
                <div className="text-size-small text-colour-white text-weight-semibold">What are insights?</div>
                <div className="spacer-tiny"></div>
                <div className="text-size-small text-weight-regular">
                  Insights help you to understand how competitors are appearing in AI search results so that you can create
                  your own ranking strategies.
                  <br />
                  <br />
                  Insights update regularly (we check every 24 hours), come back soon for more insights.
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  const getAuditScorePillTone = (rating: AnalysisResult['overallRating']) => {
    if (rating === 'Perfect') return 'is-perfect';
    if (rating === 'Excellent') return 'is-excellent';
    if (rating === 'Good') return 'is-good';
    if (rating === 'OK') return 'is-ok';
    return 'is-poor';
  };

  const renderAuditPanel = () => (
    <div className={`card audit-card${auditResult ? ' is-complete' : ''}`}>
      <div className="small-card is-audit">
        <div className="audit-top">
          <div className="embed-icon" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 7V5a2 2 0 0 1 2-2h2"></path>
              <path d="M17 3h2a2 2 0 0 1 2 2v2"></path>
              <path d="M21 17v2a2 2 0 0 1-2 2h-2"></path>
              <path d="M7 21H5a2 2 0 0 1-2-2v-2"></path>
              <circle cx="12" cy="12" r="3"></circle>
              <path d="m16 16-1.9-1.9"></path>
            </svg>
          </div>
          <div className="text-size-regular text-colour-dark-brown text-weight-semibold">
            {auditResult ? 'AI Audit Results' : 'Get your free AI audit'}
          </div>
        </div>
        <div className="line-divider is-horizontal"></div>
        <div className="insight-content">
          <div className="text-size-regular text-weight-medium">
            {auditResult ? `${workspaceBrandName} Audit` : 'AI website audit'}
          </div>
          <div className="spacer-xtiny"></div>
          <div className="text-colour-dark-grey text-size-small">
            {auditResult
              ? 'Here is an overview of your audit results. Click the button below to take a deeper dive.'
              : 'Run an AI site audit to see how visible your site is to AI.'}
          </div>

          {auditResult ? (
            <>
              <div className="spacer-small"></div>
              <div className="scores-box">
                <div className={`score-pill ${getAuditScorePillTone(auditResult.overallRating)}`}>
                  <div
                    className="audit-score"
                    style={{ ['--audit-progress' as string]: `${Math.max(0, Math.min(100, auditResult.overallScore)) * 3.6}deg` }}
                  ></div>
                  <div>{auditResult.overallScore} {auditResult.overallRating.toLowerCase()}</div>
                </div>
                <div className="score-pill is-fixes">
                  <div>
                    <span className="fix-span">{auditRecommendationItemCount}</span>{' '}
                    issues need attention
                  </div>
                </div>
              </div>
            </>
          ) : null}

          <div className="spacer-xsmall"></div>
          <button
            type="button"
            className="button is-small w-button"
            onClick={() => navigate(auditResult ? '/audit' : '/audit?autorun=1')}
          >
            {auditResult ? 'See audit results' : 'Run audit now'}
          </button>
        </div>
      </div>
    </div>
  );

  const renderListRecommendationCard = (card: InsightCard) => {
    const status = getRecommendationStatus(card.key);
    const categoryLabel = getInsightCategoryLabel(card);
    const isNew = showNewBadges && status === 'recommended';
    const primaryPrompt = card.promptTexts?.[0] ?? null;

    return (
      <div key={card.key} className="card is-insight-suggestion">
        <div className="small-card insight-card">
          <div className="insight-top">
            <div className="text-size-regular text-colour-dark-brown text-weight-semibold">
              {isNew ? <span className="new-span">New</span> : null}
              {isNew ? ' ' : null}
              {categoryLabel}
            </div>
            <div
              className="modal-close is-dark-btn is-insight-close"
              onClick={recommendationStateMutation.isPending ? undefined : () => openDeleteCardModal(card)}
              aria-label={`Remove ${card.title}`}
              role="button"
              tabIndex={recommendationStateMutation.isPending ? -1 : 0}
              style={{
                cursor: recommendationStateMutation.isPending ? 'not-allowed' : 'pointer',
                opacity: recommendationStateMutation.isPending ? 0.5 : 1,
              }}
              onKeyDown={(event) => {
                if (recommendationStateMutation.isPending) return;
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openDeleteCardModal(card);
                }
              }}
            >
              <div className="rotated-text">+</div>
            </div>
          </div>
          <div className="line-divider is-horizontal"></div>
          <div className="insight-content">
            <div className="text-size-regular text-weight-medium">{card.title}</div>
            <div className="spacer-xtiny"></div>
            <div className="text-size-regular text-colour-dark-grey">
              {card.body}
              {card.sourceUrl ? (
                <>
                  {' '}
                  <a href={card.sourceUrl} target="_blank" rel="noreferrer" className="inner-link">
                    {card.sourceUrl}
                  </a>
                </>
              ) : null}
            </div>
            {primaryPrompt ? (
              <>
                <div className="spacer-xsmall"></div>
                <div className="prompt-item">
                  <div className="text-size-xsmall text-weight-medium">
                    Prompt: <span className="text-weight-regular text-colour-alt-grey">{primaryPrompt}</span>
                  </div>
                </div>
              </>
            ) : null}
          </div>
          <div className="insight-end">
            <div className="button-wrapper">
              <div className="flexbox">
                <button
                  type="button"
                  className="button is-tiny w-button"
                  onClick={() => {
                    if (isAuditRecommendation(card)) {
                      navigate('/audit?autorun=1');
                      return;
                    }
                    openInsightDetail(card);
                  }}
                >
                  {isAuditRecommendation(card) ? 'Run audit now' : 'See full insight ->'}
                </button>
              </div>
              <button
                type="button"
                className="button is-tiny is-white has-inner-shadow is-button-flex"
                onClick={() => handleListDoneAction(card.key)}
                disabled={recommendationStateMutation.isPending || status === 'completed'}
              >
                <div className="embed-icon" aria-hidden="true">
                  <svg width="15" height="10" viewBox="0 0 21 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18.5087 0.765625L6.93892 11.7051L2.10651 7.27186L0.59375 8.9177L6.19655 14.0582L6.96694 14.7656L7.72332 14.0513L20.0495 2.39744L18.5087 0.765625Z" fill="currentColor"></path>
                  </svg>
                </div>
                <div>{status === 'completed' ? 'Completed' : 'Done'}</div>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderKanbanRecommendationCard = (card: InsightCard) => {
    const status = getRecommendationStatus(card.key);
    const categoryLabel = getInsightCategoryLabel(card);
    const isNew = showNewBadges && status === 'recommended';
    const primaryPrompt = card.promptTexts?.[0] ?? null;

    return (
    <div
      key={card.key}
      className={`card is-insight-suggestion insights-recommendation-card is-kanban-card${
        draggedCardKey === card.key ? ' is-dragging' : ''
      }`}
      draggable
      onDragStart={(event) => {
        setDraggedCardKey(card.key);
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', card.key);

        clearDragPreview();
        const cardRect = event.currentTarget.getBoundingClientRect();
        const preview = event.currentTarget.cloneNode(true) as HTMLElement;
        preview.style.position = 'fixed';
        preview.style.top = '-9999px';
        preview.style.left = '-9999px';
        preview.style.width = `${cardRect.width}px`;
        preview.style.padding = '10px';
        preview.style.background = 'transparent';
        preview.style.boxSizing = 'content-box';
        preview.style.opacity = '1';
        preview.style.pointerEvents = 'none';
        preview.style.zIndex = '9999';
        preview.classList.add('insights-drag-preview');

        const previewCard = preview.querySelector('.small-card.insight-card') as HTMLElement | null;
        if (previewCard) {
          previewCard.style.transform = 'rotate(2.4deg) translateY(-4px)';
          previewCard.style.transformOrigin = 'center center';
          previewCard.style.boxShadow = '0 20px 40px rgba(12, 19, 29, 0.18)';
        }

        document.body.appendChild(preview);
        dragPreviewRef.current = preview;

        const dragOffsetX = Math.max(0, Math.min(cardRect.width + 20, event.clientX - cardRect.left + 10));
        const dragOffsetY = Math.max(0, Math.min(cardRect.height + 20, event.clientY - cardRect.top + 10));
        event.dataTransfer.setDragImage(preview, dragOffsetX, dragOffsetY);
      }}
      onDragEnd={() => {
        setDraggedCardKey(null);
        setActiveDropColumn(null);
        clearDragPreview();
      }}
    >
      <div className="small-card insight-card">
        <div className="insight-top">
          <div className="text-size-regular text-colour-dark-brown text-weight-semibold">
            {isNew ? <span className="new-span">New</span> : null}
            {isNew ? ' ' : null}
            {categoryLabel}
          </div>
          <div
            className="modal-close is-dark-btn is-insight-close"
            onClick={recommendationStateMutation.isPending ? undefined : () => openDeleteCardModal(card)}
            aria-label={`Remove ${card.title}`}
            role="button"
            tabIndex={recommendationStateMutation.isPending ? -1 : 0}
            style={{
              cursor: recommendationStateMutation.isPending ? 'not-allowed' : 'pointer',
              opacity: recommendationStateMutation.isPending ? 0.5 : 1,
            }}
            onKeyDown={(event) => {
              if (recommendationStateMutation.isPending) return;
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                openDeleteCardModal(card);
              }
            }}
          >
            <div className="rotated-text">+</div>
          </div>
        </div>
        <div className="line-divider is-horizontal"></div>
        <div className="insight-content">
          <div className="text-size-regular text-weight-medium">{card.title}</div>
          <div className="spacer-xtiny"></div>
          <div className="text-size-regular text-colour-dark-grey">
            {card.body}
            {card.sourceUrl ? (
              <>
                {' '}
                <a href={card.sourceUrl} target="_blank" rel="noreferrer" className="inner-link">
                  {card.sourceUrl}
                </a>
              </>
            ) : null}
          </div>
          {primaryPrompt ? (
            <>
              <div className="spacer-xsmall"></div>
              <div className="prompt-item">
                <div className="text-size-xsmall text-weight-medium">
                  Prompt: <span className="text-weight-regular text-colour-alt-grey">{primaryPrompt}</span>
                </div>
              </div>
            </>
          ) : null}
        </div>
        <div className="insight-end">
          <div className="button-wrapper">
            <div className="flexbox">
              <button
                type="button"
                className="button is-tiny w-button"
                onClick={() => {
              if (isAuditRecommendation(card)) {
                navigate('/audit?autorun=1');
                return;
              }
                  openInsightDetail(card);
                }}
              >
                {isAuditRecommendation(card) ? 'Run audit now' : 'See full insight ->'}
              </button>
            </div>
            <button
              type="button"
              className="button is-tiny is-white has-inner-shadow is-button-flex"
              onClick={() => handleStatusAction(card.key)}
              disabled={recommendationStateMutation.isPending}
            >
              <div className="embed-icon" aria-hidden="true">
                <svg width="15" height="10" viewBox="0 0 21 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18.5087 0.765625L6.93892 11.7051L2.10651 7.27186L0.59375 8.9177L6.19655 14.0582L6.96694 14.7656L7.72332 14.0513L20.0495 2.39744L18.5087 0.765625Z" fill="currentColor"></path>
                </svg>
              </div>
              <div>{getStatusActionLabel(card.key)}</div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
  };

  return (
    <div className="page-wrapper">
      <div className="spacer-xsmall"></div>
      <div className="overview-header" style={{ marginBottom: '1.5rem' }}>
        <div className="overview-header-left">
          <h1>
            Insights{' '}
            <span className="insights-date-label">{insightsDateLabel}</span>
          </h1>
        </div>
        <div className={`overview-chart-toggle insights-view-toggle ${viewMode === 'kanban' ? 'is-timeline' : ''}`}>
          {(['list', 'kanban'] as RecommendationView[]).map((mode) => (
            <button
              key={mode}
              type="button"
              className={`overview-chart-toggle-button insights-view-toggle-button ${
                viewMode === mode ? 'is-active' : ''
              }`}
              onClick={() => setViewMode(mode)}
              aria-label={mode === 'list' ? 'List view' : 'Kanban view'}
            >
              {renderViewIcon(mode)}
            </button>
          ))}
        </div>
      </div>

      {viewMode === 'list' ? (
        <div className="grid-wrapper">
          <div className="insight-list">
            <div className="insights-left-wrapper">
              {renderHeroPanel()}
              {renderAuditPanel()}
            </div>

            <div className="recommendation-wrapper">
              {listVisibleEntries.length === 0 ? (
                <div className="card is-insight-suggestion">
                  <div className="small-card insight-card">
                    <div className="insight-content">
                      <div className="text-size-regular text-weight-medium">No insights available</div>
                      <div className="spacer-xtiny"></div>
                      <div className="text-size-regular text-colour-dark-grey">
                        New competitor and source-specific insights will appear here as your prompt runs update.
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {todaysRecommendationCards.length > 0 ? (
                    <>
                      <div className="text-size-regular text-weight-semibold" style={{ marginBottom: '-0.3rem' }}>
                        Today&apos;s recommendations for {workspaceBrandName}{' '}
                        <span className="number-pill">{todaysRecommendationCards.length}</span>
                      </div>
                      {todaysRecommendationCards.map((card) => renderListRecommendationCard(card))}
                      {paginatedListVisibleCards.length > 0 ? (
                        <div className="text-size-regular text-weight-semibold" style={{ marginTop: '0.5rem', marginBottom: '-0.3rem' }}>
                          Previous recommendations for {workspaceBrandName}{' '}
                          <span className="number-pill">{previousRecommendationEntries.length}</span>
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <div className="text-size-regular text-weight-semibold" style={{ marginBottom: '-0.3rem' }}>
                      Recommendations for {workspaceBrandName}{' '}
                      <span className="number-pill">{activeRecommendationCount}</span>
                    </div>
                  )}

                  {paginatedListVisibleCards.map((card) => renderListRecommendationCard(card))}
                  {hasInsightsPagination ? (
                    <div className="pagination insights-pagination">
                      <button
                        type="button"
                        className="button is-small is-white has-inner-shadow w-button"
                        onClick={() => setInsightsPage((page) => Math.max(1, page - 1))}
                        disabled={insightsPage === 1}
                      >
                        Previous
                      </button>
                      <div className="text-size-small text-weight-medium text-colour-alt-grey">
                        Page {insightsPage} of {totalInsightPages}
                      </div>
                      <button
                        type="button"
                        className="button is-small is-white has-inner-shadow w-button"
                        onClick={() => setInsightsPage((page) => Math.min(totalInsightPages, page + 1))}
                        disabled={insightsPage === totalInsightPages}
                      >
                        Next
                      </button>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="insights-board">
          <div className="insights-kanban-top">
            {renderHeroPanel(true)}
            {renderAuditPanel()}
          </div>

          <div className="insights-recommendations-header">
            <div className="table-title-wrapper is-left">
              <div className="dashboard-panel-title">Recommendations for {workspaceBrandName}</div>
              <div className="number-pill">{activeRecommendationCount}</div>
            </div>
          </div>

          {visibleCards.length === 0 ? (
            <div className="dashboard-panel insights-recommendation-card">
              <div className="dashboard-panel-body">
                <div className="dashboard-panel-title">No insights available</div>
                <div className="spacer-xxsmall"></div>
                <div className="text-size-small text-colour-alt-grey">
                  New competitor and source-specific insights will appear here as your prompt runs update.
                </div>
              </div>
            </div>
          ) : (
            <div className="insights-board-columns">
              {([
                ['recommended', 'Recommended'],
                ['in_progress', 'In Progress'],
                ['completed', 'Completed'],
              ] as Array<[RecommendationStatus, string]>).map(([status, label]) => (
                <div
                  key={status}
                  className={`dashboard-panel insights-board-column${
                    activeDropColumn === status ? ' is-drop-target' : ''
                  }`}
                  onDragOver={(event) => {
                    event.preventDefault();
                    if (draggedCardKey) {
                      setActiveDropColumn(status);
                    }
                  }}
                  onDragLeave={() => {
                    if (activeDropColumn === status) {
                      setActiveDropColumn(null);
                    }
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    const droppedKey = event.dataTransfer.getData('text/plain') || draggedCardKey;
                    if (droppedKey) {
                      void updateRecommendationStatus(droppedKey, status);
                    }
                    setDraggedCardKey(null);
                    setActiveDropColumn(null);
                  }}
                >
                  <div className="dashboard-panel-body insights-board-column-body">
                    <div className="insights-board-column-header">
                      <div className="dashboard-panel-title">{label}</div>
                      <div className="number-pill">{visibleEntriesByStatus[status].length}</div>
                    </div>

                    <div className="insights-board-column-list">
                      {visibleEntriesByStatus[status].length === 0 ? (
                        <div className="insights-board-empty text-size-small text-colour-alt-grey">
                          No recommendations here yet.
                        </div>
                      ) : (
                        visibleEntriesByStatus[status].map((entry) => renderKanbanRecommendationCard(entry.card))
                      )}
                      {draggedCardKey && activeDropColumn === status ? <div className="insights-drop-placeholder"></div> : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setCardToDelete(null);
        }}
        onConfirm={handleConfirmDeleteCard}
        title="Remove recommendation?"
        message={
          cardToDelete
            ? `This recommendation will be removed from today’s Insights board: "${cardToDelete.title}"`
            : 'This recommendation will be removed from today’s Insights board.'
        }
        confirmText="Yes, remove"
        cancelText="No, cancel"
        loading={recommendationStateMutation.isPending}
      />
      <DeleteModal
        isOpen={isCompleteModalOpen}
        onClose={() => {
          setIsCompleteModalOpen(false);
          setCardToComplete(null);
        }}
        onConfirm={handleConfirmCompleteCard}
        title="Mark insight as done?"
        message={
          cardToComplete
            ? `This insight will be moved to Completed: "${cardToComplete.title}"`
            : 'This insight will be moved to Completed.'
        }
        confirmText="Yes, mark done"
        cancelText="No, cancel"
        loading={recommendationStateMutation.isPending}
      />
      <InsightDetailModal item={activeInsight} onClose={() => setActiveInsight(null)} />
      <div className="spacer-large"></div>
    </div>
  );
}
